import { FirebaseOptions, initializeApp } from 'firebase/app';
import { Firestore, getFirestore, doc, setDoc } from 'firebase/firestore';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { v4 as uuidV4 } from 'uuid';
import FIREBASE_CONFIG, { FIREBASE_EVENT_NAME } from '@/configs/firebase';
import { REGEX_EMAIL } from '@/configs/regex';

export interface Props {
  eventNameConfig?: string;
  firebaseConfig?: FirebaseOptions;
}

export default function useFirestore({
  eventNameConfig: originEventNameConfig,
  firebaseConfig: originFirebaseConfig,
}: Props) {
  // ref
  const firebaseAppDb = useRef<Firestore>();

  // memo
  const eventNameConfig = useMemo(
    () => originEventNameConfig ?? FIREBASE_EVENT_NAME,
    [originEventNameConfig]
  );
  const firebaseConfig = useMemo(
    () => originFirebaseConfig ?? FIREBASE_CONFIG,
    [originFirebaseConfig]
  );
  const isActive = useMemo(() => {
    let result = false;

    try {
      result = !!(
        firebaseConfig.apiKey &&
        firebaseConfig.authDomain &&
        firebaseConfig.projectId &&
        firebaseConfig.storageBucket &&
        firebaseConfig.messagingSenderId &&
        firebaseConfig.appId
      );
    } catch (error) {}

    return result;
  }, [firebaseConfig]);

  // callback
  const postData = useCallback(
    async (
      data: { [key: string]: any },
      eventName: string = eventNameConfig
    ) => {
      if (!(firebaseAppDb.current && isActive)) {
        return;
      }

      await setDoc(doc(firebaseAppDb.current, 'events', uuidV4()), {
        ...data,
        createAt: new Date().getTime(),
        event_name: eventName,
      });
    },
    [isActive, eventNameConfig]
  );
  const postEmail = useCallback(
    async (email: string, eventName?: string) => {
      if (!(firebaseAppDb.current && REGEX_EMAIL.test(email))) {
        return;
      }

      await postData({ email }, eventName);
    },
    [postData]
  );

  // effect
  useEffect(() => {
    if (!firebaseAppDb.current) {
      firebaseAppDb.current = getFirestore(initializeApp(firebaseConfig));
    }
  }, [firebaseConfig]);

  return { firebaseAppDb: firebaseAppDb.current, postData, postEmail };
}
